// The 3D geology model: a stratigraphic-height field on a regular grid (trilinear inside cells) plus the
// stratigraphic column that turns heights into formations.  Also builds the 3D views of it: vertical
// cross-sections ("curtains") and formation contact surfaces.
import * as THREE from 'three';

export class GeoModel {
  constructor(meta, buf) {
    this.meta = meta;
    const q = new Int16Array(buf), v = new Float32Array(q.length), { offset, scale } = meta;
    for (let i = 0; i < q.length; i++) v[i] = offset + q[i] * scale;
    this.v = v;
    [this.nz, this.ny, this.nx] = meta.shape;
    this.o = meta.origin; this.s = meta.step;
    this.units = meta.units;
    this.tops = meta.units.slice(0, -1).map(u => u.top);
    this.colors = meta.units.map(u => new THREE.Color(u.color));
    this.rgb = meta.units.map(u => [1, 3, 5].map(i => parseInt(u.color.slice(i, i + 2), 16)));
    this.zmin = this.o[2]; this.zmax = this.o[2] + (this.nz - 1) * this.s[2];
    this.ground = null;                                   // DEM height on each model column, from the server
    if (meta.ground) {
      const b = atob(meta.ground.data), u = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i);
      const g = new Int16Array(u.buffer); this.ground = new Float32Array(g.length);
      for (let i = 0; i < g.length; i++) this.ground[i] = g[i] * meta.ground.scale;
    }
  }

  // stratigraphic height at a point (metres above the reference horizon)
  value(E, N, Z) {
    const { o, s, nx, ny, nz, v } = this;
    let fx = (E - o[0]) / s[0], fy = (N - o[1]) / s[1], fz = (Z - o[2]) / s[2];
    fx = Math.min(Math.max(fx, 0), nx - 1.0001); fy = Math.min(Math.max(fy, 0), ny - 1.0001); fz = Math.min(Math.max(fz, 0), nz - 1.0001);
    const i = Math.floor(fx), j = Math.floor(fy), k = Math.floor(fz), u = fx - i, w = fy - j, t = fz - k;
    const a = (k * ny + j) * nx + i, sy = nx, sz = nx * ny;
    const c00 = v[a] * (1 - u) + v[a + 1] * u, c10 = v[a + sy] * (1 - u) + v[a + sy + 1] * u;
    const c01 = v[a + sz] * (1 - u) + v[a + sz + 1] * u, c11 = v[a + sz + sy] * (1 - u) + v[a + sz + sy + 1] * u;
    return (c00 * (1 - w) + c10 * w) * (1 - t) + (c01 * (1 - w) + c11 * w) * t;
  }

  unit(val) { const T = this.tops; let i = 0; while (i < T.length && val > T[i]) i++; return i; }
  unitAt(E, N, Z) { return this.unit(this.value(E, N, Z)); }

  // "Tonoloway Limestone, ~12 m above its base"
  describe(val) {
    const k = this.unit(val), u = this.units[k];
    const base = k > 0 ? this.tops[k - 1] : null, top = k < this.tops.length ? this.tops[k] : null;
    let where = '';
    if (base != null && top != null) {
      const above = val - base, below = top - val;
      where = above <= below ? `~${Math.max(0, Math.round(above))} m above its base` : `~${Math.max(0, Math.round(below))} m below its top`;
    } else if (base != null) where = `~${Math.round(val - base)} m above its base`;
    else if (top != null) where = `~${Math.round(top - val)} m below its top`;
    const near = (this.meta.markers || []).filter(m => Math.abs(m.value - val) < 8).map(m => m.name);
    return { k, unit: u, where, near };
  }

  inside(E, N) {
    const { o, s, nx, ny } = this;
    return E >= o[0] && N >= o[1] && E <= o[0] + (nx - 1) * s[0] && N <= o[1] + (ny - 1) * s[1];
  }

  // Elevation of the contact `top` (unit k's top) on each model column: the first crossing scanning down
  // from the top of the model, so it is also found where erosion has removed it (the mesh hides those
  // parts against the ground).  NaN where the column never crosses it.
  contactSurface(top) {
    const { nx, ny, nz, o, s, v } = this, out = new Float32Array(nx * ny).fill(NaN);
    for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
      let fPrev = v[((nz - 1) * ny + j) * nx + i];
      if (fPrev <= top) { out[j * nx + i] = this.zmax + 300; continue; }   // above the model (and the ground): keeps the edge smooth
      for (let k = nz - 2; k >= 0; k--) {
        const f = v[(k * ny + j) * nx + i];
        if (f <= top) { out[j * nx + i] = o[2] + (k + (top - f) / (fPrev - f || 1)) * s[2]; break; }
        fPrev = f;
      }
    }
    return out;
  }
}

// ------------------------------------------------------------------ 3D objects
// A vertical section from A to B (easting/northing), coloured by formation, from `zBase` up to the ground.
export function sectionMesh(geo, A, B, ground, zBase, local) {
  const L = Math.hypot(B[0] - A[0], B[1] - A[1]);
  const nCol = ground.length, top = Math.max(...ground) + 2, bot = Math.max(geo.zmin, zBase);
  const W = Math.min(2048, Math.max(64, Math.round(L / 4))), H = Math.min(1024, Math.max(64, Math.round((top - bot) / 1.5)));
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const g2 = cv.getContext('2d'), img = g2.createImageData(W, H), d = img.data;
  const idx = new Int16Array(W * H);
  for (let c = 0; c < W; c++) {
    const t = (c + 0.5) / W, E = A[0] + (B[0] - A[0]) * t, N = A[1] + (B[1] - A[1]) * t;
    const gf = t * (nCol - 1), gi = Math.min(nCol - 2, Math.floor(gf)), gz = ground[gi] + (ground[gi + 1] - ground[gi]) * (gf - gi);
    for (let r = 0; r < H; r++) {
      const Z = top - (r + 0.5) / H * (top - bot);
      idx[r * W + c] = Z > gz ? -1 : geo.unitAt(E, N, Z);
    }
  }
  for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) {
    const p = r * W + c, k = idx[p], o = 4 * p;
    if (k < 0) { d[o + 3] = 0; continue; }
    const edge = (c > 0 && idx[p - 1] >= 0 && idx[p - 1] !== k) || (r > 0 && idx[p - W] >= 0 && idx[p - W] !== k);
    const rgb = geo.rgb[k], f = edge ? 0.3 : 1;
    d[o] = rgb[0] * f; d[o + 1] = rgb[1] * f; d[o + 2] = rgb[2] * f; d[o + 3] = 255;
  }
  g2.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
  // geometry: a strip following the ground profile on top, flat at the base
  const pos = [], uv = [], ix = [];
  for (let i = 0; i < nCol; i++) {
    const t = i / (nCol - 1), E = A[0] + (B[0] - A[0]) * t, N = A[1] + (B[1] - A[1]) * t;
    const p0 = local(E, N, bot), p1 = local(E, N, ground[i] + 0.3);
    pos.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z);
    uv.push(t, 0, t, (ground[i] + 0.3 - bot) / (top - bot));
    if (i) { const a = 2 * (i - 1); ix.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2)); geom.setIndex(ix);
  const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide, transparent: true, alphaTest: 0.5, depthWrite: true });
  const mesh = new THREE.Mesh(geom, mat); mesh.renderOrder = 1;
  return mesh;
}

// A formation contact as a sheet under the whole area, hidden where it would be above the ground.
// `xf` = [xc, yc, zc]: the scene origin, to map local coordinates back onto the model grid.
export function surfaceMesh(geo, heights, color, local, opacity, xf) {
  const { nx, ny, o, s } = geo, pos = new Float32Array(nx * ny * 3), ix = [];
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    const p = j * nx + i, z = heights[p], q = local(o[0] + i * s[0], o[1] + j * s[1], Number.isFinite(z) ? z : 0);
    pos[3 * p] = q.x; pos[3 * p + 1] = q.y; pos[3 * p + 2] = q.z;
  }
  const ok = (p) => Number.isFinite(heights[p]);
  for (let j = 0; j < ny - 1; j++) for (let i = 0; i < nx - 1; i++) {
    const a = j * nx + i, b = a + 1, c = a + nx, d = c + 1;
    if (ok(a) && ok(b) && ok(c)) ix.push(a, c, b);
    if (ok(b) && ok(c) && ok(d)) ix.push(b, c, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setIndex(ix); g.computeVertexNormals();
  const m = new THREE.MeshLambertMaterial({ color, side: THREE.DoubleSide, transparent: opacity < 1, opacity, depthWrite: opacity >= 1 });
  if (geo.ground) {
    // ground heights on the model columns as a half-float texture, sampled with linear filtering, so the
    // line where a contact meets the ground is smooth rather than stepped
    if (!geo.groundTex) {
      const h = new Uint16Array(geo.ground.length);
      for (let i = 0; i < h.length; i++) h[i] = THREE.DataUtils.toHalfFloat(geo.ground[i]);
      const t = new THREE.DataTexture(h, nx, ny, THREE.RedFormat, THREE.HalfFloatType);
      t.magFilter = t.minFilter = THREE.LinearFilter; t.needsUpdate = true;
      geo.groundTex = t;
    }
    const [xc, yc, zc] = xf;
    const u = { gTex: { value: geo.groundTex }, gXf: { value: new THREE.Vector4(xc - o[0], (nx - 1) * s[0], yc - o[1], (ny - 1) * s[1]) },
      gZc: { value: zc }, gN: { value: new THREE.Vector2(nx, ny) } };
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, u);
      sh.vertexShader = 'varying vec3 vLocalP;\n' + sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvLocalP = position;');
      sh.fragmentShader = 'uniform sampler2D gTex; uniform vec4 gXf; uniform float gZc; uniform vec2 gN; varying vec3 vLocalP;\n'
        + sh.fragmentShader.replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
          vec2 gUv = vec2((vLocalP.x + gXf.x) / gXf.y, (-vLocalP.z + gXf.z) / gXf.w);
          gUv = (gUv * (gN - 1.0) + 0.5) / gN;
          if (vLocalP.y + gZc > texture2D(gTex, gUv).r - 0.5) discard;`);
    };
  }
  const mesh = new THREE.Mesh(g, m); mesh.renderOrder = 1.5;
  return mesh;
}
